import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { 
  Scale, Shuffle, Truck, Package, Camera, CreditCard,
  CheckCircle, AlertTriangle, Clock, Shield, Eye, 
  Crown, BadgeCheck, Ban, FileText
} from 'lucide-react';

// Rule Section Component
const RuleSection = ({ number, title, icon: Icon, gradient, children }) => (
  <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-8 overflow-hidden">
    {/* Gradient accent */}
    <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${gradient}`} />
    
    {/* Number badge */}
    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r ${gradient} text-black font-black text-xl mb-6`}>
      {number}
    </div>
    
    <div className="flex items-start gap-4 mb-6">
      <Icon className="w-8 h-8 text-white flex-shrink-0 mt-1" />
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    
    <div className="space-y-4">
      {children}
    </div>
  </div>
);

// Rule Item Component
const RuleItem = ({ icon: Icon, title, description, warning = false }) => (
  <div className={`flex items-start gap-3 p-4 rounded-xl ${warning ? 'bg-red-500/10 border border-red-500/20' : 'bg-zinc-800/50'}`}>
    <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${warning ? 'text-red-400' : 'text-emerald-400'}`} />
    <div>
      <p className={`font-medium ${warning ? 'text-red-400' : 'text-white'}`}>{title}</p>
      <p className="text-zinc-400 text-sm mt-1">{description}</p>
    </div>
  </div>
);

function MarketplaceRulesPage() {
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-6">
              <Scale className="w-4 h-4" />
              Marketplace Guidelines
            </div>
            
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Marketplace Rules &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                Fair Play
              </span>
            </h1>
            
            <p className="text-xl text-zinc-400 leading-relaxed">
              Welcome to the Slab Society Marketplace. To ensure a premium experience for every collector, 
              all users must adhere to the following guidelines.
            </p>
          </div>
        </div>
      </section>

      {/* Rules Grid */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Rule 1: Fair Visibility Shuffle */}
          <RuleSection 
            number="1" 
            title="The Fair-Visibility Shuffle"
            icon={Shuffle}
            gradient="from-[#00BFFF] to-blue-600"
          >
            <RuleItem
              icon={Eye}
              title="Small Seller Priority"
              description="Our marketplace uses a 'Weighted Shuffle' logic. Listings from collectors with fewer than 5 active items are automatically prioritized in the 15-second homepage rotation to ensure every slab gets seen."
            />
            <RuleItem
              icon={Clock}
              title="Spotlight Rotation"
              description="The featured listings refresh every 15 seconds. Manipulating your browser or using bots to stay in the spotlight is strictly prohibited and may result in a shadow-ban from the featured row."
              warning
            />
          </RuleSection>

          {/* Rule 2: Shipping & Fulfillment */}
          <RuleSection 
            number="2" 
            title="Shipping & Fulfillment (Powered by Shippo)"
            icon={Truck}
            gradient="from-emerald-400 to-emerald-600"
          >
            <RuleItem
              icon={Clock}
              title="Prompt Shipping"
              description="Sellers are expected to ship items within 2 business days of a successful sale."
            />
            <RuleItem
              icon={BadgeCheck}
              title="Verified Labels"
              description="Sellers must use the 'Print Label' feature in their Seller Hub. This ensures the address is validated and the tracking number is automatically shared with the buyer for total transparency."
            />
            <RuleItem
              icon={Package}
              title="Tracking Required"
              description="All shipments must include valid tracking. Shipping 'pwe' (plain white envelope) for high-value slabs is not permitted unless explicitly agreed upon in the listing."
              warning
            />
          </RuleSection>

          {/* Rule 3: Listing Accuracy */}
          <RuleSection 
            number="3" 
            title="Listing Accuracy & Grading"
            icon={FileText}
            gradient="from-purple-400 to-purple-600"
          >
            <RuleItem
              icon={CheckCircle}
              title="Truth in Grading"
              description="If a card is raw, do not list it as a 'Potential 10.' Describe the condition accurately."
            />
            <RuleItem
              icon={Camera}
              title="Photo Standards"
              description="You must provide clear, high-resolution photos of the front and back of the slab. Using 'stock photos' for unique collectibles is a violation of our trust policy."
              warning
            />
          </RuleSection>

          {/* Rule 4: Payments */}
          <RuleSection 
            number="4" 
            title="Payments & Gold Membership"
            icon={CreditCard}
            gradient="from-amber-400 to-orange-500"
          >
            <RuleItem
              icon={Shield}
              title="Secure Checkout"
              description="All transactions must go through the Slab Society Stripe checkout. 'Off-platform' deals are not protected by our buyer/seller guarantees."
            />
            <RuleItem
              icon={Crown}
              title="Gold Tier Promo"
              description="The SLAB100 code is valid for first-time Gold members only. Creating duplicate accounts to redeem the free month multiple times will result in account suspension."
              warning
            />
          </RuleSection>

        </div>
      </section>

      {/* Summary Box */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Scale className="w-8 h-8 text-amber-400" />
              <h2 className="text-2xl font-bold text-white">Fair Play Commitment</h2>
            </div>
            <p className="text-lg text-zinc-300 leading-relaxed mb-6">
              These rules exist to protect our community of collectors. Violations may result in warnings, 
              listing removals, or account suspension depending on severity. We review all reports within 
              24-48 hours.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/trust"
                className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl transition-all"
              >
                View Trust & Safety
              </Link>
              <Link 
                to="/qa"
                className="px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl border border-zinc-700 transition-all"
              >
                Read FAQ
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">
            Questions about these guidelines?{' '}
            <Link to="/qa" className="text-amber-400 hover:underline">Visit our FAQ</Link>
            {' '}or{' '}
            <Link to="/messages" className="text-amber-400 hover:underline">contact support</Link>.
          </p>
          <p className="text-zinc-600 text-xs mt-4">
            Last updated: March 2026 | Slab Society Trust & Safety Team
          </p>
        </div>
      </section>
    </div>
  );
}

export default MarketplaceRulesPage;
